/**
 * Content for the media inquiries pages `/media-inquiries` (EN) and
 * `/tr/basin-talepleri` (TR).
 *
 * The contact list holds OXORY's own press contacts only — no third-party
 * journalist data is published here.
 */

import { PRESS_FACTS } from "./press-kit";

export interface PressContact {
  name: string;
  role: string;
  topics: string;
  email: string;
  phone?: string;
  phoneHref?: string;
  language: string;
}

export interface MediaInquiryCopy {
  eyebrow: string;
  title: string;
  intro: string;
  formHeading: string;
  formLead: string;
  fields: {
    name: string;
    outlet: string;
    email: string;
    topic: string;
    deadline: string;
    deadlineHint: string;
    message: string;
  };
  topics: string[];
  submit: string;
  submitting: string;
  successToast: string;
  successText: string;
  errorText: string;
  privacyNote: string;
  contactsHeading: string;
  contactsLead: string;
  contactLabels: { topics: string; language: string };
  responseHeading: string;
  responseItems: string[];
  pressKitLabel: string;
}

const EMAIL = PRESS_FACTS.email;

export const MEDIA_CONTACTS_EN: PressContact[] = [
  {
    name: PRESS_FACTS.managingDirector,
    role: "Managing Director and official spokesperson",
    topics:
      "Company strategy, SAP delivery and shoring, partner network, product roadmap (AI DEVGUI, AI IDOCMON)",
    email: EMAIL,
    phone: PRESS_FACTS.phone,
    phoneHref: PRESS_FACTS.phoneHref,
    language: "German, English, Turkish",
  },
  {
    name: "OXORY press desk",
    role: "Press and media requests",
    topics:
      "Interview scheduling, quotes, company facts, logo and image files, press release distribution",
    email: EMAIL,
    language: "German, English, Turkish",
  },
  {
    name: "OXORY compliance desk",
    role: "Subject-matter contact, e-invoicing and regulations",
    topics:
      "German e-invoicing mandate, XRechnung, ZUGFeRD, Peppol, SAP-integrated compliance projects",
    email: EMAIL,
    language: "German, English",
  },
];

export const MEDIA_CONTACTS_TR: PressContact[] = [
  {
    name: PRESS_FACTS.managingDirector,
    role: "Genel Müdür ve resmî sözcü",
    topics:
      "Şirket stratejisi, SAP delivery ve shoring, uzman ağı, ürün yol haritası (AI DEVGUI, AI IDOCMON)",
    email: EMAIL,
    phone: PRESS_FACTS.phone,
    phoneHref: PRESS_FACTS.phoneHref,
    language: "Almanca, İngilizce, Türkçe",
  },
  {
    name: "OXORY basın masası",
    role: "Basın ve medya talepleri",
    topics:
      "Röportaj planlaması, alıntılar, şirket bilgileri, logo ve görsel dosyaları, basın bülteni dağıtımı",
    email: EMAIL,
    language: "Almanca, İngilizce, Türkçe",
  },
  {
    name: "OXORY uyumluluk masası",
    role: "Konu uzmanı, e-fatura ve mevzuat",
    topics:
      "Almanya e-fatura zorunluluğu, XRechnung, ZUGFeRD, Peppol, SAP entegre uyumluluk projeleri",
    email: EMAIL,
    language: "Almanca, İngilizce",
  },
];

export const MEDIA_EN: MediaInquiryCopy = {
  eyebrow: "Media inquiries",
  title: "Media inquiries and press contacts",
  intro:
    "One place for journalists, analysts and editors to reach OXORY. Send your request through the form and it lands directly in our press inbox — or contact the right person below.",
  formHeading: "Send a media inquiry",
  formLead:
    "Tell us your outlet, topic and deadline. We reply to the email address you provide, usually within one business day.",
  fields: {
    name: "Your name",
    outlet: "Outlet or publication",
    email: "Email",
    topic: "Type of request",
    deadline: "Deadline",
    deadlineHint: "For example: 12 March, end of day",
    message: "Your request",
  },
  topics: [
    "Interview request",
    "Quote or statement",
    "Background briefing",
    "Company facts and figures",
    "Logo, images or media kit",
    "Speaker or event request",
    "Other media request",
  ],
  submit: "Send media inquiry",
  submitting: "Sending…",
  successToast: "Thanks — your media inquiry is on its way.",
  successText:
    "Thanks — your inquiry reached our press inbox. We reply to the email address you provided.",
  errorText: `Sending failed. Please email ${EMAIL} directly.`,
  privacyNote:
    "We use your details only to answer your media request. See our privacy notice for how we handle enquiry data.",
  contactsHeading: "Who to talk to",
  contactsLead: "OXORY press contacts by topic.",
  contactLabels: { topics: "Topics", language: "Languages" },
  responseHeading: "What to expect",
  responseItems: [
    "We confirm receipt and name a contact person, usually within one business day.",
    "Urgent deadlines: mark them in the form and we prioritise your request.",
    "Quotes are approved by the named spokesperson before publication.",
    "Company facts, boilerplate texts and logo files are available in the press kit.",
  ],
  pressKitLabel: "Open the press kit",
};

export const MEDIA_TR: MediaInquiryCopy = {
  eyebrow: "Basın talepleri",
  title: "Basın talepleri ve iletişim kişileri",
  intro:
    "Gazeteciler, analistler ve editörler için OXORY'ye ulaşmanın tek adresi. Talebinizi formdan gönderin, doğrudan basın kutumuza düşsün — ya da aşağıdaki doğru kişiyle iletişime geçin.",
  formHeading: "Basın talebi gönderin",
  formLead:
    "Yayınınızı, konuyu ve teslim tarihinizi belirtin. Verdiğiniz e-posta adresine genellikle bir iş günü içinde yanıt veriyoruz.",
  fields: {
    name: "Adınız",
    outlet: "Yayın veya kurum",
    email: "E-posta",
    topic: "Talep türü",
    deadline: "Teslim tarihi",
    deadlineHint: "Örneğin: 12 Mart, gün sonu",
    message: "Talebiniz",
  },
  topics: [
    "Röportaj talebi",
    "Alıntı veya açıklama",
    "Arka plan bilgilendirmesi",
    "Şirket bilgileri ve rakamlar",
    "Logo, görsel veya medya kiti",
    "Konuşmacı veya etkinlik talebi",
    "Diğer basın talepleri",
  ],
  submit: "Basın talebini gönder",
  submitting: "Gönderiliyor…",
  successToast: "Teşekkürler — basın talebiniz yola çıktı.",
  successText:
    "Teşekkürler — talebiniz basın kutumuza ulaştı. Verdiğiniz e-posta adresine yanıt vereceğiz.",
  errorText: `Gönderim başarısız oldu. Lütfen doğrudan ${EMAIL} adresine yazın.`,
  privacyNote:
    "Bilgilerinizi yalnızca basın talebinizi yanıtlamak için kullanıyoruz. Ayrıntılar için gizlilik bildirimimize bakın.",
  contactsHeading: "Kiminle görüşmeli?",
  contactsLead: "Konularına göre OXORY basın iletişim kişileri.",
  contactLabels: { topics: "Konular", language: "Diller" },
  responseHeading: "Neler bekleyebilirsiniz?",
  responseItems: [
    "Talebinizi genellikle bir iş günü içinde onaylar ve ilgili kişiyi bildiririz.",
    "Acil teslim tarihleri: formda belirtin, talebinizi öncelikli ele alalım.",
    "Alıntılar, yayından önce ilgili sözcü tarafından onaylanır.",
    "Şirket bilgileri, tanıtım metinleri ve logo dosyaları basın kitinde yer alıyor.",
  ],
  pressKitLabel: "Basın kitini açın",
};
